import { useEffect, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Pager } from '@/components/ui';
import { CHANGELOG, isCurrentChangelog, type ChangelogEntry } from '@/lib/changelog';
import { forceRefresh } from '@/lib/appUpdate';

/**
 * 업데이트 내역. (2026-08-19, 2026-08-30 전면 개편)
 *
 * 홈 화면 하단의 버전을 누르면 열린다.
 *
 * 🔴 왜 게시판 형태로 바꿨나 (2026-08-30 사용자 피드백)
 *   그전에는 전 버전의 모든 항목(62개)을 한 화면에 이어 붙였다. 그래서
 *     ① 폰에서 시트가 화면을 꽉 채워 위쪽 둥근 모서리가 화면 밖으로 밀렸다
 *     ② 🔴 닫기 버튼이 없어 배경을 눌러야 했는데, 그 배경이 보이지 않았다.
 *        폰에는 ESC 도 없다 - **사실상 닫을 방법이 없었다**
 *     ③ 안쪽 패딩이 없어 글자가 화면 가장자리에 붙었다
 *
 *   목록 → 상세로 나누면 한 화면에 담기는 양이 줄어 셋이 함께 풀린다.
 *   헤더는 고정이라 어디까지 스크롤해도 닫기가 보인다.
 *
 * 🔴 내역은 코드와 함께 배포된다
 *   lib/changelog.ts 에 있다. DB 에 두면 배포와 내역이 따로 놀고,
 *   롤백하면 내역만 남는다.
 */

/** 한 페이지에 보여줄 버전 수. 폰에서 스크롤 없이 담기는 양이다. */
const PAGE_SIZE = 5;

interface Props {
  open: boolean;
  onClose: () => void;
  /** 화면 하단에 뜨는 버전. 2026.08.19.5 형식 */
  currentVersion: string | null;
}

export function ChangelogModal({ open, onClose, currentVersion }: Props) {
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<ChangelogEntry | null>(null);

  // 닫았다 다시 열면 처음부터 본다. 지난번에 보던 상세가 남아 있으면 당황스럽다
  useEffect(() => {
    if (!open) {
      setPage(0);
      setSelected(null);
    }
  }, [open]);

  const totalPages = Math.ceil(CHANGELOG.length / PAGE_SIZE);
  const shown = CHANGELOG.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Modal open={open} onClose={onClose} maxWidth="sm:max-w-lg" labelledBy="changelog-title">
      {/*
        🔴 헤더를 고정한다
          상세는 스무 줄이 넘을 수 있다. 헤더가 함께 밀려 올라가면 폰에서
          닫으려고 맨 위까지 되돌아가야 한다.
      */}
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-surface px-4 py-3">
        {selected !== null && (
          <button
            type="button"
            onClick={() => setSelected(null)}
            aria-label="목록으로"
            className="-ml-1 rounded-lg px-2 py-1 text-sm text-fg-muted transition-colors hover:bg-surface-hover"
          >
            ←
          </button>
        )}
        <h2 id="changelog-title" className="flex-1 text-base font-semibold text-fg">
          {selected === null ? '업데이트 내역' : selected.date}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="rounded-lg px-2 py-1 text-fg-subtle transition-colors hover:bg-surface-hover hover:text-fg-muted"
        >
          ✕
        </button>
      </div>

      {selected === null ? (
        /*
          🔴 페이지가 바뀌어도 높이를 유지한다 (2026-08-30 사용자 피드백)

          마지막 페이지는 항목이 적어(7개 버전 / 5개씩이면 2페이지는 2건)
          모달이 갑자기 쪼그라들었다. 열고 넘기는 동안 창 크기가 널뛰면
          그것만으로 만듦새가 떨어져 보인다.

          카드 하나가 대략 5rem 이라 5건이면 25rem. 거기에 아래 안내와
          페이저 자리를 더해 최소 높이를 잡아 둔다. 넘치면 그때는 늘어난다.
        */
        <div className="flex min-h-[30rem] flex-col p-4">
          <ul className="space-y-2">
            {shown.map((entry) => (
              <li key={entry.version}>
                <button
                  type="button"
                  onClick={() => setSelected(entry)}
                  className={
                    // 터치 타깃을 넉넉히 - 폰에서 손가락으로 누른다
                    'flex w-full items-center gap-3 rounded-xl border border-line px-3 py-3 ' +
                    'text-left transition-colors hover:bg-surface-hover'
                  }
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="text-sm font-semibold text-fg">{entry.date}</span>
                      {isCurrentChangelog(entry.version, currentVersion) && (
                        <span className="rounded-full bg-brand-weak px-2 py-0.5 text-[11px] text-brand">
                          지금 쓰는 버전
                        </span>
                      )}
                      <span className="text-xs text-fg-subtle">{entry.changes.length}건</span>
                    </div>
                    {/* 첫 줄만 미리 보여준다. 두 줄에서 자른다 */}
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-fg-muted">
                      {entry.changes[0]}
                    </p>
                  </div>
                  <span aria-hidden className="shrink-0 text-fg-subtle">
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* mt-auto - 항목이 적은 페이지에서도 페이저와 안내가 아래에 붙어 있는다 */}
          <div className="mt-auto pt-3">
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          </div>

          <p className="mt-4 border-t border-line pt-3 text-xs leading-relaxed text-fg-subtle">
            내부 구조 변경은 여기 적지 않습니다. 화면에서 달라지는 것만 적습니다.
          </p>

          {/*
            🔴 자동 갱신이 안 걸렸을 때의 탈출구 (2026-08-30 여기로 옮겼다)
              그전에는 홈 화면 버전 아래에 늘 떠 있었다. 버전이 최신인지
              알 방법이 없던 때(89번)의 잔재다. 지금은 version.json 으로
              대조하므로 평소에는 띄울 이유가 없고, 홈에서는 어긋났을 때만 뜬다.
              여기 남겨 두는 것은 그 대조마저 실패했을 때를 위해서다.
          */}
          <button
            type="button"
            onClick={() => void forceRefresh()}
            className="mt-3 text-xs text-fg-subtle underline decoration-dotted underline-offset-2 hover:text-fg-muted"
          >
            최신으로 다시 받기
          </button>
        </div>
      ) : (
        // 상세도 같은 최소 높이를 쓴다. 목록에서 넘어올 때 창이 출렁이지 않는다
        <div className="min-h-[30rem] p-4">
          {isCurrentChangelog(selected.version, currentVersion) && (
            <span className="mb-3 inline-block rounded-full bg-brand-weak px-2 py-0.5 text-[11px] text-brand">
              지금 쓰는 버전
            </span>
          )}
          <ul className="space-y-3">
            {selected.changes.map((change) => (
              <li key={change} className="flex gap-2">
                <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-fg-subtle" />
                <span className="text-sm leading-relaxed text-fg-muted">{change}</span>
              </li>
            ))}
          </ul>

          {/*
            그날 오간 질문과 답. (2026-09-10 사용자 요청)

            🔴 왜 변경 내역과 나눠 두나
              위는 "무엇이 바뀌었나" 이고 아래는 "무엇을 묻고 무엇을 알아냈나" 다.
              코드가 안 바뀌어도 남는 것들이 있다 - 재봤더니 지금이 나아서 그대로
              둔 결정, 잘못 알고 있다가 확인해서 정정한 것, 안 하기로 한 것과 그 이유.
              섞어 놓으면 "바뀐 것" 을 훑으러 온 사람이 긴 글에 막힌다.

            질문을 접어 두지 않고 펼쳐 둔다 - 접으면 여기에 무엇이 있는지 모른 채
            지나가고, 그러면 없는 것과 같다.
          */}
          {selected.qna && selected.qna.length > 0 && (
            <div className="mt-6 border-t border-line pt-4">
              <h3 className="mb-3 text-xs font-semibold text-fg-muted">
                오간 이야기 {selected.qna.length}건
              </h3>
              <ul className="space-y-4">
                {selected.qna.map((item) => (
                  <li key={item.q}>
                    <p className="text-sm font-medium leading-relaxed text-fg">
                      <span aria-hidden className="mr-1 text-fg-subtle">Q</span>
                      {item.q}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-fg-muted">
                      <span aria-hidden className="mr-1 text-fg-subtle">A</span>
                      {item.a}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
